import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FileText,
  MessageSquareMore,
  RefreshCw,
  Star,
  UserRoundPlus
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  fetchProfessionalReservationSlots,
  updateProfessionalReservationAbsent,
  updateProfessionalReservationComment,
  updateProfessionalReservationLate,
  type ProfessionalReservationClient,
  type ProfessionalReservationSlot
} from "../lib/professionalReservationsApi";
import "../styles/reservas.css";

type ReservationTab = "week" | "month";
type ReservationShift = "morning" | "afternoon";

type DaySlots = {
  date: string;
  count: number;
  slots: ProfessionalReservationSlot[];
};

function toDateOnly(value: Date) {
  const next = new Date(value);
  next.setHours(12, 0, 0, 0);
  return next;
}

function formatYmd(value: Date) {
  return toDateOnly(value).toISOString().slice(0, 10);
}

function parseYmd(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
}

function addDays(base: Date, amount: number) {
  const next = new Date(base);
  next.setDate(next.getDate() + amount);
  return toDateOnly(next);
}

function addMonths(base: Date, amount: number) {
  const next = new Date(base);
  next.setMonth(next.getMonth() + amount);
  next.setDate(1);
  return toDateOnly(next);
}

function mondayOf(base: Date) {
  const next = toDateOnly(base);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(next, diff);
}

function monthDays(base: Date) {
  const start = new Date(base.getFullYear(), base.getMonth(), 1, 12, 0, 0, 0);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0, 12, 0, 0, 0);
  const out: Date[] = [];
  for (let current = start; current <= end; current = addDays(current, 1)) {
    out.push(new Date(current));
  }
  return out;
}

function stateIcon(status?: string | null) {
  switch (status) {
    case "Muy bien":
      return "⏫";
    case "Bien":
      return "⬆";
    case "Normal":
      return "➖";
    case "Mal":
      return "⬇";
    case "Muy mal":
      return "⏬";
    default:
      return "";
  }
}

function normalizeError(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function CommentModal(props: {
  open: boolean;
  value: string;
  submitting: boolean;
  onClose: () => void;
  onChange: (next: string) => void;
  onSubmit: () => void;
}) {
  if (!props.open) {
    return null;
  }

  return (
    <div className="pro-res-modal-backdrop" role="presentation">
      <div className="pro-res-modal" role="dialog" aria-modal="true" aria-labelledby="reservation-comment-title">
        <div className="pro-res-modal-header">
          <div>
            <span className="pro-res-modal-kicker">Reserva</span>
            <h3 id="reservation-comment-title">Comentario interno</h3>
          </div>
          <button type="button" className="pro-res-modal-close" onClick={props.onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <textarea
          className="pro-res-modal-textarea"
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
          placeholder="Añade aquí contexto útil para esta reserva"
        />

        <div className="pro-res-modal-actions">
          <button type="button" className="pro-res-button pro-res-button-secondary" onClick={props.onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="pro-res-button pro-res-button-primary"
            onClick={props.onSubmit}
            disabled={props.submitting}
          >
            {props.submitting ? "Guardando..." : "Guardar comentario"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SlotCard(props: {
  slot: ProfessionalReservationSlot;
  onComment: (client: ProfessionalReservationClient) => void;
  onLateToggle: (client: ProfessionalReservationClient) => void;
  onAbsentToggle: (client: ProfessionalReservationClient) => void;
}) {
  const navigate = useNavigate();
  const serviceLabel = props.slot.label ? <span className="pro-res-slot-service">{props.slot.label}</span> : null;

  return (
    <article className="pro-res-slot-card">
      <header className="pro-res-slot-header">
        <div>
          <div className="pro-res-slot-time">{props.slot.time}</div>
          {serviceLabel}
        </div>
        <div className="pro-res-slot-count">
          {props.slot.count} reserva{props.slot.count === 1 ? "" : "s"}
        </div>
      </header>

      <div className="pro-res-client-list">
        {props.slot.clients.map((client) => (
          <div
            key={client.bookingId}
            className={[
              "pro-res-client-row",
              client.hasDocuments ? "has-documents" : "",
              client.isRecent ? "is-recent" : "",
              client.absent ? "is-absent" : ""
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="pro-res-client-main">
              <button
                type="button"
                className="pro-res-client-name"
                onClick={() => navigate(`/clientes?customerId=${client.id}`)}
              >
                {client.name}
              </button>

              <div className="pro-res-client-meta">
                {client.trainingFactor !== null && client.trainingFactor !== undefined ? (
                  <span>Factor {String(client.trainingFactor)}</span>
                ) : null}
                {client.questionnaireStatus ? (
                  <span>{stateIcon(client.questionnaireStatus)} {client.questionnaireStatus}</span>
                ) : null}
                {client.comment ? <span>Con comentario</span> : null}
              </div>

              <div className="pro-res-client-tags">
                {client.isVip ? (
                  <span className="pro-res-tag vip">
                    <Star size={12} />
                    VIP
                  </span>
                ) : null}
                {client.hasDocuments ? (
                  <span className="pro-res-tag docs">
                    <FileText size={12} />
                    Docs
                  </span>
                ) : null}
                {client.isRecent ? (
                  <span className="pro-res-tag recent">
                    <UserRoundPlus size={12} />
                    Nuevo
                  </span>
                ) : null}
                {client.absent ? (
                  <span className="pro-res-tag absent">
                    <CircleAlert size={12} />
                    Ausente
                  </span>
                ) : null}
              </div>
            </div>

            <div className="pro-res-client-actions">
              <button
                type="button"
                className="pro-res-action-button"
                onClick={() => props.onComment(client)}
                title={client.comment ? "Editar comentario" : "Añadir comentario"}
              >
                <MessageSquareMore size={16} />
              </button>
              <button
                type="button"
                className={`pro-res-toggle-button ${client.late ? "active" : ""}`}
                onClick={() => props.onLateToggle(client)}
              >
                Tarde
              </button>
              <button
                type="button"
                className={`pro-res-toggle-button ${client.absent ? "active" : ""}`}
                onClick={() => props.onAbsentToggle(client)}
              >
                Ausente
              </button>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

export default function ReservasPage() {
  const [tab, setTab] = useState<ReservationTab>("week");
  const [shift, setShift] = useState<ReservationShift>("morning");
  const [selectedDate, setSelectedDate] = useState<Date>(() => toDateOnly(new Date()));
  const [weekSlots, setWeekSlots] = useState<ProfessionalReservationSlot[]>([]);
  const [monthSlots, setMonthSlots] = useState<DaySlots[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalClient, setModalClient] = useState<ProfessionalReservationClient | null>(null);
  const [commentValue, setCommentValue] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);

  const selectedDateIso = useMemo(() => formatYmd(selectedDate), [selectedDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(mondayOf(selectedDate), index)), [selectedDate]);

  const weekLabel = useMemo(() => {
    const monday = mondayOf(selectedDate);
    const sunday = addDays(monday, 6);
    return `${format(monday, "d LLL", { locale: es })} - ${format(sunday, "d LLL", { locale: es })}`;
  }, [selectedDate]);

  const monthLabel = useMemo(() => format(selectedDate, "LLLL yyyy", { locale: es }), [selectedDate]);

  const weekTotal = useMemo(() => weekSlots.reduce((sum, slot) => sum + slot.count, 0), [weekSlots]);
  const monthTotal = useMemo(() => monthSlots.reduce((sum, day) => sum + day.count, 0), [monthSlots]);

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const slots = await fetchProfessionalReservationSlots({
        date: selectedDateIso,
        shift
      });
      setWeekSlots(slots);
      setMonthSlots([]);
    } catch (requestError) {
      setWeekSlots([]);
      setError(normalizeError(requestError, "No se pudieron cargar las reservas del turno seleccionado"));
    } finally {
      setLoading(false);
    }
  }, [selectedDateIso, shift]);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const days = monthDays(selectedDate);
      const result = await Promise.all(
        days.map(async (day) => {
          try {
            const slots = await fetchProfessionalReservationSlots({
              date: formatYmd(day),
              shift
            });

            return {
              date: formatYmd(day),
              count: slots.reduce((sum, slot) => sum + slot.count, 0),
              slots
            } satisfies DaySlots;
          } catch {
            return {
              date: formatYmd(day),
              count: 0,
              slots: []
            } satisfies DaySlots;
          }
        })
      );
      setMonthSlots(result);
      setWeekSlots([]);
    } catch (requestError) {
      setMonthSlots([]);
      setError(normalizeError(requestError, "No se pudo construir el resumen mensual de reservas"));
    } finally {
      setLoading(false);
    }
  }, [selectedDate, shift]);

  useEffect(() => {
    if (tab === "week") {
      void fetchWeek();
    } else {
      void fetchMonth();
    }
  }, [fetchMonth, fetchWeek, selectedDateIso, shift, tab]);

  function openCommentModal(client: ProfessionalReservationClient) {
    setModalClient(client);
    setCommentValue(client.comment || "");
  }

  async function submitComment() {
    if (!modalClient) {
      return;
    }

    setCommentSaving(true);
    setError(null);

    try {
      await updateProfessionalReservationComment(modalClient.bookingId, commentValue);
      setModalClient(null);
      setCommentValue("");
      if (tab === "week") {
        await fetchWeek();
      } else {
        await fetchMonth();
      }
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo guardar el comentario"));
    } finally {
      setCommentSaving(false);
    }
  }

  async function toggleLate(client: ProfessionalReservationClient) {
    const actionKey = `late-${client.bookingId}`;
    setActionLoadingKey(actionKey);
    setError(null);
    try {
      await updateProfessionalReservationLate(client.bookingId, !client.late);
      if (tab === "week") {
        await fetchWeek();
      } else {
        await fetchMonth();
      }
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo actualizar el estado de tarde"));
    } finally {
      setActionLoadingKey(null);
    }
  }

  async function toggleAbsent(client: ProfessionalReservationClient) {
    const actionKey = `absent-${client.bookingId}`;
    setActionLoadingKey(actionKey);
    setError(null);
    try {
      await updateProfessionalReservationAbsent(client.bookingId, !client.absent);
      if (tab === "week") {
        await fetchWeek();
      } else {
        await fetchMonth();
      }
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo actualizar el estado de ausencia"));
    } finally {
      setActionLoadingKey(null);
    }
  }

  return (
    <main className="shell-page">
      <section className="pro-res-hero shell-hero-card">
        <div className="shell-hero-icon">
          <CalendarDays size={22} />
        </div>
        <div className="shell-hero-copy">
          <span className="shell-eyebrow">Operativa diaria</span>
          <h1>Reservas</h1>
          <p>Consulta tus reservas por turnos, revisa el estado de cada cliente y registra incidencias sin salir de la app.</p>
        </div>
      </section>

      <section className="pro-res-panel">
        <div className="pro-res-toolbar">
          <div className="pro-res-segmented">
            <button
              type="button"
              className={tab === "week" ? "active" : ""}
              onClick={() => setTab("week")}
            >
              Semanal
            </button>
            <button
              type="button"
              className={tab === "month" ? "active" : ""}
              onClick={() => setTab("month")}
            >
              Mensual
            </button>
          </div>

          <div className="pro-res-segmented">
            <button
              type="button"
              className={shift === "morning" ? "active" : ""}
              onClick={() => setShift("morning")}
            >
              Mañana
            </button>
            <button
              type="button"
              className={shift === "afternoon" ? "active" : ""}
              onClick={() => setShift("afternoon")}
            >
              Tarde
            </button>
          </div>

          <button
            type="button"
            className="pro-res-refresh-button"
            onClick={() => void (tab === "week" ? fetchWeek() : fetchMonth())}
          >
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>

        <div className="pro-res-subheader">
          <div>
            <div className="pro-res-subtitle">{tab === "week" ? weekLabel : monthLabel}</div>
            <div className="pro-res-helper">
              {tab === "week" ? "Vista diaria del turno seleccionado" : "Resumen del mes por días"}
            </div>
          </div>
          <div className="pro-res-total-pill">
            {tab === "week" ? weekTotal : monthTotal} reservas
          </div>
        </div>

        {tab === "week" ? (
          <div className="pro-res-week-strip">
            <button type="button" className="pro-res-nav-button" onClick={() => setSelectedDate(addDays(selectedDate, -7))}>
              <ChevronLeft size={16} />
            </button>

            <div className="pro-res-week-days">
              {weekDays.map((day) => {
                const active = formatYmd(day) === selectedDateIso;
                return (
                  <button
                    key={formatYmd(day)}
                    type="button"
                    className={`pro-res-day-button ${active ? "active" : ""}`}
                    onClick={() => setSelectedDate(day)}
                  >
                    <span>{format(day, "EEE", { locale: es })}</span>
                    <strong>{format(day, "d")}</strong>
                  </button>
                );
              })}
            </div>

            <button type="button" className="pro-res-nav-button" onClick={() => setSelectedDate(addDays(selectedDate, 7))}>
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="pro-res-month-nav">
            <button type="button" className="pro-res-nav-button" onClick={() => setSelectedDate(addMonths(selectedDate, -1))}>
              <ChevronLeft size={16} />
            </button>
            <strong>{monthLabel}</strong>
            <button type="button" className="pro-res-nav-button" onClick={() => setSelectedDate(addMonths(selectedDate, 1))}>
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </section>

      {error ? (
        <section className="pro-res-error-box">
          <CircleAlert size={18} />
          <span>{error}</span>
        </section>
      ) : null}

      {loading ? (
        <section className="pro-res-list">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="pro-res-skeleton" />
          ))}
        </section>
      ) : null}

      {!loading && tab === "week" ? (
        <section className="pro-res-list">
          {weekSlots.length === 0 ? (
            <div className="pro-res-empty">No hay reservas para este turno.</div>
          ) : (
            weekSlots.map((slot) => (
              <SlotCard
                key={slot.id}
                slot={slot}
                onComment={openCommentModal}
                onLateToggle={(client) => {
                  if (!actionLoadingKey) {
                    void toggleLate(client);
                  }
                }}
                onAbsentToggle={(client) => {
                  if (!actionLoadingKey) {
                    void toggleAbsent(client);
                  }
                }}
              />
            ))
          )}
        </section>
      ) : null}

      {!loading && tab === "month" ? (
        <section className="pro-res-month-grid">
          {monthSlots.map((day) => {
            const date = parseYmd(day.date);
            return (
              <button
                key={day.date}
                type="button"
                className="pro-res-month-card"
                onClick={() => {
                  setSelectedDate(date);
                  setTab("week");
                }}
              >
                <div className="pro-res-month-card-header">
                  <div>
                    <strong>{format(date, "EEE d", { locale: es })}</strong>
                    <span>{format(date, "LLL yyyy", { locale: es })}</span>
                  </div>
                  <div className="pro-res-slot-count">{day.count} res.</div>
                </div>

                <div className="pro-res-month-preview">
                  {day.slots.slice(0, 3).map((slot) => (
                    <div key={slot.id} className="pro-res-month-preview-row">
                      <span>{slot.time}</span>
                      <span>{slot.count}</span>
                    </div>
                  ))}
                  {day.slots.length > 3 ? (
                    <span className="pro-res-month-more">+{day.slots.length - 3} horas</span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </section>
      ) : null}

      <CommentModal
        open={Boolean(modalClient)}
        value={commentValue}
        submitting={commentSaving}
        onClose={() => {
          setModalClient(null);
          setCommentValue("");
        }}
        onChange={setCommentValue}
        onSubmit={() => void submitComment()}
      />
    </main>
  );
}
